
import { Component, Vue, Prop } from 'vue-property-decorator';
// import { Color } from '@/interfaces/components/color/Color';
import { ColorPowder } from '@/interfaces/components/color/ColorPowder';
import { ColorTableData } from '@/interfaces/components/color/ColorTableData';
import { RoltekColorCollections, MappedColorCodes } from '@/helpers/ColorModule/ColorModuleHelper';
import { ColorChart } from '@/interfaces/components/color/ColorChart';

@Component({
    name: 'ColorModuleTable',
    components: {},
})
export default class ColorModuleTable extends Vue {
    @Prop({ required: false }) private searchQuery!: string;
    @Prop({ required: true }) private currentCollection!: string;
    @Prop({ required: false, default: false }) private showLatestColors!: boolean;
    @Prop({ required: true }) private pagination!: any;

    private tableHeight: string = '575px';

    private get customColumns(): Array<any> {
        return [
            {
                condition: 'STANDARD-COLLECTION',
                order: 1,
                columnData: { title: this.$t('Kod'), dataIndex: 'colorCode', key: 'colorCode', width: 70 },
            },
        ];
    }

    private get colors(): Array<ColorPowder> {
        return this.$store.getters['colorData/colors'];
    }

    private get latestColors(): Array<{ color: any; powder: ColorPowder }> {
        return this.$store.getters['colorData/latestColors'];
    }

    private onTableChange(pagination: any, filters: any, sorter: any) {
        this.$emit('tableChange', pagination, filters, sorter);
    }

    private calculateTableHeight() {
        const baseHeight = 575;
        const baseMargin = 80;

        const tableEL = document.querySelector('.color-module-table');

        if (!tableEL) {
            return baseHeight + 'px';
        }

        const tableRect = tableEL.getBoundingClientRect();
        const tableHeight = tableRect.height;
        const tableMarginTop = parseInt(getComputedStyle(tableEL).marginTop, 10);

        if (tableMarginTop > baseMargin) {
            const newHeight = tableHeight + (tableMarginTop - baseMargin);

            return newHeight + 'px';
        }

        return baseHeight + 'px';
    }

    removePaginationTooltips() {
        const paginationItems = document.querySelectorAll(
            '.ant-pagination-item, .ant-pagination-prev, .ant-pagination-next',
        );
        paginationItems.forEach((item) => {
            item.removeAttribute('title'); // Nuke the tooltip behavior
        });
    }

    private get columns() {
        const columns = [
            {
                dataIndex: 'color',
                key: 'color',
                scopedSlots: { customRender: 'color' },
                slots: { title: 'colorTitle' }, // Use `slots` for title
                width: 70,
            },
            {
                dataIndex: 'colorName',
                key: 'colorName',
                scopedSlots: { customRender: 'colorName' },
                slots: { title: 'colorNameTitle' }, // Use `slots` for title
                width: 300,
            },
            { title: this.$t('Gloss/Surface'), dataIndex: 'colorSurface', key: 'colorSurface', width: 150 },
            { title: this.$t('Manufacturer’s powder ID'), dataIndex: 'powderId', key: 'powderId', width: 370 },
            {
                title: this.$t(''),
                dataIndex: 'select',
                key: 'select',
                scopedSlots: { customRender: 'select' },
                width: 80,
            },
        ];

        // Insert custom columns based on conditions
        this.customColumns.forEach((customCol) => {
            if (customCol.condition === 'STANDARD-COLLECTION' && this.currentCollection === 'STANDARD') {
                columns.splice(customCol.order, 0, customCol.columnData);
            }
        });

        return columns;
    }

    private get colorsData() {
        // If showLatestColors is ON and we have latest colors, use them
        if (this.showLatestColors) {
            return this.formatLatestColorsData();
        }

        // Otherwise use regular colors
        if (!this.colors.length) {
            return [];
        }

        const colors: ColorTableData[] = [];

        for (let i = 0; i < this.colors.length; i++) {
            const powder = this.colors[i];
            const color = powder?.colors ? powder.colors[0] : null;

            const groupName = powder?.colorCollection?.groupName || '';
            const isRoltekColor =
                this.determineIsValidRoltekCollection(powder.colorCollection) && !!color?.ingeniousColor;
            const isStandard = isRoltekColor && groupName === 'STANDARD';
            const ingeniousColor = color?.ingeniousColor || '';
            const colorCode = MappedColorCodes[ingeniousColor] || '';
            const colorSurface = powder?.colorSurface?.name ?? '-';

            if ((this.currentCollection === 'STANDARD' && isStandard) || !isStandard || !this.currentCollection) {
                colors.push({
                    key: powder.id + '-' + color?.id,
                    id: powder.id,
                    color: color?.hex,
                    colorName: color?.name,
                    powderId: powder?.name,
                    colorSurface,
                    groupName,
                    isRoltekColor,
                    isStandard,
                    ingeniousColor,
                    colorObject: color,
                    colorCode,
                });
            }
        }

        if (!this.searchQuery) {
            return colors;
        }

        return this.filterColorsBySeachQuery(colors);
    }

    private formatLatestColorsData(): ColorTableData[] {
        if (!this.latestColors || this.latestColors.length === 0) {
            return [];
        }

        const colors: ColorTableData[] = [];

        for (let i = 0; i < this.latestColors.length; i++) {
            const latestColor = this.latestColors[i];
            const powder = latestColor.powder;
            const color = latestColor.color;

            const groupName = powder?.colorCollection?.groupName || '';
            const isRoltekColor =
                this.determineIsValidRoltekCollection(powder.colorCollection) && !!color?.ingeniousColor;
            const isStandard = isRoltekColor && groupName === 'STANDARD';
            const ingeniousColor = color?.ingeniousColor || '';
            const colorCode = MappedColorCodes[ingeniousColor] || '';
            const colorSurface = powder?.colorSurface?.name ?? '-';

            colors.push({
                key: powder.id + '-' + color?.id,
                id: powder.id,
                color: color?.hex,
                colorName: color?.name,
                powderId: powder?.name,
                colorSurface,
                groupName,
                isRoltekColor,
                isStandard,
                ingeniousColor,
                colorObject: color,
                colorCode,
            });
        }

        if (!this.searchQuery) {
            return colors;
        }

        return this.filterColorsBySeachQuery(colors);
    }

    private determineIsValidRoltekCollection(colorChart: ColorChart | null | undefined): boolean {
        if (!colorChart) {
            return false;
        }

        const collectionName = colorChart?.groupName || '';

        return RoltekColorCollections.has(collectionName as string);
    }

    private filterColorsBySeachQuery(colors: ColorTableData[]) {
        return colors.filter((color: ColorTableData) => {
            switch (true) {
                case color?.colorName?.toLowerCase().includes(this.searchQuery.toLowerCase()):
                    return true;
                case color?.colorSurface?.toLowerCase().includes(this.searchQuery.toLowerCase()):
                    return true;
                case color?.powderId?.toLowerCase().includes(this.searchQuery.toLowerCase()):
                    return true;
                default:
                    return false;
            }
        });
    }

    private selectColor(selectedColor: ColorTableData) {
        let selectedPowder;

        if (this.showLatestColors && this.latestColors && this.latestColors.length > 0) {
            // Find the powder from latestColors
            const latestColor = this.latestColors.find((lc) => lc.powder.id === selectedColor.id);
            selectedPowder = latestColor?.powder;
        } else {
            // Find the powder from regular colors
            selectedPowder = this.colors.find((powder) => powder.id === selectedColor.id);
        }

        this.$emit('selectColor', {
            color: selectedColor.colorObject,
            selectedPowder,
            isRoltekColor: selectedColor.isRoltekColor,
            groupName: selectedColor.groupName,
        });
    }

    private handleResize() {
        this.tableHeight = this.calculateTableHeight();
    }

    private mounted() {
        this.removePaginationTooltips();
        window.addEventListener('resize', this.handleResize);
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    }
}
